import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import {
  SG_GET_CHAT_ROOM_V2,
  SG_CREATE_CHAT_ROOM_V2,
  SG_FETCH_CHAT_ROOM_V2,
  SG_DELETE_CHAT_ROOM,
  DISCONNECT_WEBSOCKET_V2,
  SAGA_CONNECT_WEBSOCKET_V2,
  MESSAGE_WEBSOCKET,
  SAGA_MESSAGE_WEBSOCKET_V2,
  CLEAR_CHAT_MESSAGES,
  CLEAR_CREATED_ROOM,
  CHAT_ROOM_PAGE,
  SET_NEW_FILTERS,
  START_NEW_CHAT,
  CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD,
  SET_RAW_DATA,
  SET_FILTERED_DATA,
  SET_FILTERED_LAST_DATA,
  SET_DATA_DATES,
  SET_NAVIGATION,
} from "constants/actions";

import {
  generateTopicName,
  addPromptToHistoryIfNeeded,
} from "../chatHelpers";
import useAnalyticsDatasetFlag from "../useAnalyticsDatasetFlag";
import { useLanguage } from "hooks/useLanguage";

// Import calculation functions from NewAnalytics dependencies
import {
  checkPersonality,
  convert_personality_to_categories,
} from "app/audit/calculations";

import {
  filter_data,
  get_dates,
  get_most_recent,
  sort_historical_data,
  build_default_State,
  get_last_responses,
} from "reports/Audit/data_navigation/calculate_data";

const getPromptObjectDataFromParams = (params) => {
  // Example parser for "file" or "pulse" data sources
  const fileCardData = params?.dataSources?.filter(
    (source) => source.type === "prompt"
  );
  return fileCardData;
};

const getCardDataFromParams = (params) => {
  // Example parser for "file" or "pulse" data sources
  const fileCardData =
    params?.dataSources
      ?.filter((source) => source.type === "file")
      ?.map((source) => ({
        type: "file",
        id: source.id,
        title: source?.name,
        status: 22,
      })) || [];

  const factorCardData =
    params?.tags?.map((source) => ({
      type: "culture",
      id: source,
      title: source,
    })) || [];

  const outcomeCardData =
    params?.outcomes?.map((source) => ({
      type: "outcome",
      id: source,
      title: source,
    })) || [];

  const selectedPulses =
    params?.dataSources
      ?.filter((source) => source.type === "pulse")
      ?.map((source) => ({
        type: "pulse",
        id: source.id,
        title: source?.name || source?.title,
        status: 22,
      })) || [];

  const selectedSurveys =
    params?.dataSources
      ?.filter((source) => source.type === "survey")
      ?.map((source) => ({
        type: "survey",
        id: source.id,
        title: source?.name || source?.title,
        surveysCount: source?.surveysCount,
        lastUpdated: source?.lastUpdated,
        metadata: source?.metadata,
        status: 22,
      })) || [];

  return [
    ...fileCardData,
    ...factorCardData,
    ...outcomeCardData,
    ...selectedPulses,
    ...selectedSurveys,
  ];
};

// Create the context
const CopilotDataContext = createContext();

export const useCopilotDataContext = () => useContext(CopilotDataContext);

export const CopilotDataProvider = ({ children }) => {
  const { language } = useLanguage();
  const lastScrollPositionRef = useRef(null);
  const isSmoothScrollingRef = useRef(false);

  const dispatch = useDispatch();
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [chatUserName, setChatUserName] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ignoredMessages, setIgnoredMessages] = useState([]);

  const [chatStarted, setChatStarted] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [pendingInitialMessage, setPendingInitialMessage] = useState(null);
  const [selectedParams, setSelectedParams] = useState(null);
  const [messagesByRoom, setMessagesByRoom] = useState({});
  const [chatHistory, setChatHistory] = useState([]);
  const [initialPendingId, setInitialPendingId] = useState(null);
  const [usedPrompt, setUsedPrompt] = useState(null);
  const [messagePending, setMessagePending] = useState(false);
  const [feedbackLength, setFeedbackLength] = useState(0);
  const createdRoom = useSelector(
    (state) => state.ai_chatv2?.created,
    shallowEqual
  );

  const updatedRoom = useSelector(
    (state) => state.ai_chatv2?.updated,
    shallowEqual
  );

  const chatToken = useSelector(
    (state) => state.auth?.chat_token,
    shallowEqual
  );
  const usrEmployee = useSelector(
    (state) => state.employees?.userEmp,
    shallowEqual
  );

  const chatRooms = useSelector(
    (state) => state.ai_chatv2?.chat_topics || [],
    shallowEqual
  );
  
  const currentRoom = useSelector(
    (state) => state.ai_chatv2?.chatData,
    shallowEqual
  );

  // Get survey data from Redux (same as DataSourceDrawer)
  const {
    get_culture_audit_reports,
    get_organizations,
    pulseSurveys,
    get_auth,
    existingRawData,
    get_survey_structure,
    organizationId,
    nav_state,
    outcomesQuestionsList,
  } = useSelector(
    (state) => ({
      get_culture_audit_reports: state.debrief_schedule.culture_audit_reports,
      get_organizations: state.organizations,
      pulseSurveys: state.task_templates.scheduled_tasks?.results,
      get_auth: state.auth,
      existingRawData: state.audit?.raw_data,
      get_survey_structure: state.surveystructure.survey_structure,
      organizationId: state.selectedOrg?.organization?.id || Number(state.auth.organization_id),
      nav_state: state.audit.nav,
      outcomesQuestionsList: state.surveyquestions.outcome_questions,
    }),
    shallowEqual
  );

  const wsConnected = useSelector(
    (state) => state.ai_websocketv2?.isConnected,
    shallowEqual
  );
  const wsError = useSelector(
    (state) => state.ai_websocketv2?.error,
    shallowEqual
  );
  const messages = useSelector((state) => state.ai_websocketv2?.messages);

  const allowAnalyticsDataset = useAnalyticsDatasetFlag();
  const [shouldUseCustomDataset, setShouldUseCustomDataset] = useState(
    allowAnalyticsDataset
  );
  const initialRawDataRef = useRef(existingRawData);
  const hasCustomDataset =
    shouldUseCustomDataset &&
    Array.isArray(existingRawData) &&
    existingRawData?.[0]?.datasetMetadata;

  useEffect(() => {
    if (allowAnalyticsDataset) {
      setShouldUseCustomDataset(true);
    }
  }, [allowAnalyticsDataset]);

  useEffect(() => {
    if (initialRawDataRef.current !== existingRawData) {
      if (
        Array.isArray(existingRawData) &&
        existingRawData?.[0]?.datasetMetadata
      ) {
        setShouldUseCustomDataset(true);
      } else if (!existingRawData || existingRawData?.length === 0) {
        setShouldUseCustomDataset(false);
      }
      initialRawDataRef.current = existingRawData;
    }
  }, [existingRawData]);

  // A ref for message scrolling if needed
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    // Save current scroll position
    const currentScrollTop = document.documentElement.scrollTop;
    const currentScrollHeight = document.documentElement.scrollHeight;

    // Wait for content to potentially reflow
    setTimeout(() => {
      const newScrollHeight = document.documentElement.scrollHeight;
      // If content height changed, adjust scroll position to maintain relative position
      if (newScrollHeight > currentScrollHeight) {
        const heightDiff = newScrollHeight - currentScrollHeight;
        window.scrollTo({
          top: currentScrollTop + heightDiff,
          behavior: "smooth",
        });
      }

      // Then scroll to bottom
      if (messagesEndRef?.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 50);
  };

  const isValidMessageForChat = (data, roomId) => {
    /**
     * In your old ChatWindow code, you had logic like:
     * (!data?.web_chat_id && only one message key) ||
     *   data?.web_chat_id === room?.id ||
     *   (data?.web_chat_id && isNaN(data.web_chat_id)) ||
     *   data?.channel?.id
     *
     * Adjust it as needed. The simplest check is:
     */
    if (data?.web_chat_id && !roomId) return true;
    if (!roomId) return false;
    if (data?.web_chat_id === roomId) return true;
    return false;
  };

  const addOrUpdateMessageInContext = (roomId, newMsg) => {
    saveScrollPosition();

    const pendingUserId = `usr-${initialPendingId}`;
    // If it's a user message, we need to check if it's the pending message
    // If it is, we can skip it.
    if (newMsg.from === "user" && newMsg.id === pendingUserId) {
      return;
    }

    if (messagesByRoom?.[roomId]?.length === 1 && newMsg.from === "user") {
      const lastMessage = messagesByRoom?.[roomId]?.[0];
      if (lastMessage.from === "user" && lastMessage.status === "complete") {
        setIgnoredMessages((prev) => [...prev, lastMessage.id]);
      }
      return;
    }

    //   If it is an ignored message, we can skip it.
    if (ignoredMessages.includes(newMsg.id)) {
      return;
    }

    // Strip translation instruction from user messages (in case it comes from server)
    const displayMessage = newMsg.from === "user"
      ? stripTranslationInstruction(newMsg.message || "")
      : newMsg.message;

    setMessagesByRoom((prev) => {
      const newState = { ...prev };
      const existingRoomMessages = newState[roomId] || [];

      // Check if we already have a message with that same ID
      const index = existingRoomMessages.findIndex((m) => m.id === newMsg.id);

      if (index >= 0) {
        // Update the existing
        const old = existingRoomMessages[index];
        existingRoomMessages[index] = {
          ...old,
          message: displayMessage,
          status: newMsg.status,
          from: newMsg.from || old.from,
          type: "response", // Mark it as assistant response
        };
      } else {
        // Insert a new assistant message
        existingRoomMessages.push({
          id: newMsg.id,
          message: displayMessage,
          from: newMsg.from || "assistant",
          status: newMsg.status,
          type: "response",
        });
      }

      newState[roomId] = existingRoomMessages;
      return newState;
    });

    // After setting state, determine if we should scroll to bottom
    const shouldScrollToBottom =
      newMsg.from === "assistant" ||
      newMsg.status === "complete" ||
      newMsg.status === "streaming";

    // Restore position or scroll to bottom after state update
    // restoreScrollPosition(shouldScrollToBottom);
  };

  // -------------------------------------------------------------------
  // Main effect to handle incoming WebSocket messages
  // -------------------------------------------------------------------
  useEffect(() => {
    // If there's no connected socket or no new messages object, do nothing
    if (!wsConnected || !messages) return;

    let awaitingMessage = false;
    // Loop over the dictionary
    Object.entries(messages).forEach(([key, msg]) => {
      if (isValidMessageForChat(msg, selectedRoomId)) {
        // Override the msg's `id` with the dictionary key:
        addOrUpdateMessageInContext(selectedRoomId, {
          ...msg,
          id: key, // e.g. "usr-msg-1739991001848" or "ast-msg-1739991001848"
        });

        // If it's a user message, we need to check if it's the pending message
        // if the most recent message is a user message, then are awaiting a response
        if (msg.from === "user") {
          awaitingMessage = true;
        } else {
          awaitingMessage = false;
        }
      }
    });

    setMessagePending(awaitingMessage);
    // Scroll down after merging
    // scrollToBottom();
  }, [wsConnected, messages, selectedRoomId]);

  useEffect(() => {
    setChatUserName(usrEmployee?.first_name + " " + usrEmployee?.last_name);
  }, [usrEmployee]);

  useEffect(() => {
    if (createdRoom?.id) {
      // First update the room list
      dispatch({ type: SG_GET_CHAT_ROOM_V2, payload: `page=1` });
      dispatch({ type: CHAT_ROOM_PAGE, payload: 1 });
      setCurrentPage(1);
    }
  }, [createdRoom, dispatch]);

  useEffect(() => {
    if (updatedRoom) {
      dispatch({ type: CLEAR_CREATED_ROOM });
      dispatch({ type: SG_GET_CHAT_ROOM_V2, payload: `page=1` });
      dispatch({ type: CHAT_ROOM_PAGE, payload: 1 });
      setCurrentPage(1);
    }
  }, [dispatch, updatedRoom]);

  useEffect(() => {
    dispatch({ type: SG_GET_CHAT_ROOM_V2, payload: `page=${currentPage}` });
  }, [dispatch, currentPage]);

  // -------------------------------------------------------------------
  // Utility to strip translation instruction from message (for display)
  // -------------------------------------------------------------------
  const stripTranslationInstruction = (message) => {
    if (!message || typeof message !== 'string') return message;

    // Remove the translation instruction pattern (works for both English and French)
    const instructionPattern = /\n\nAlways return all responses in\s+(?:French|English),?\s*If returning verbatim feedback, make sure all feedback are translated into\s+(?:French|English)\.?/gi;
    return message.replace(instructionPattern, '').trim();
  };

  // -------------------------------------------------------------------
  // Utility to parse room data from the store
  // -------------------------------------------------------------------
  const handleFetchedRoomData = (roomData) => {
    const { id, interaction_history = [], params = {}, feedback_length = 0 } = roomData;

    // Check for feedback length from multiple sources
    const feedbackLengthFromParams = params?.feedback_length;
    const feedbackLengthFromRoomData = feedback_length;
    const feedbackLengthFromCustomData = hasCustomDataset ? 1 : 0;
    
    // Use the highest available feedback length
    const finalFeedbackLength = Math.max(
      feedbackLengthFromParams || 0, 
      feedbackLengthFromRoomData || 0,
      feedbackLengthFromCustomData || 0
    );
    
    setFeedbackLength(finalFeedbackLength);
    // Example: convert params into "card data"
    const paramsFormatted = getCardDataFromParams(params);
    const paramsObjectData = getPromptObjectDataFromParams(params);

    setSelectedParams(paramsFormatted);

    let presetPrompt = "";

    if (paramsObjectData?.length > 0) {
      setUsedPrompt(paramsObjectData[0]);
      const promptObject = paramsObjectData[0];
      presetPrompt = promptObject?.question;
    } else {
      setUsedPrompt(null);
    }

    // Flatten the "prompt → response" pairs
    const parsedMessages = interaction_history.flatMap((interaction, i) => {
      // Below parsedMessages creates the message history,
      // IF the presetPrompt is not empty, we can replace the initial message with the promptQuery
      // We do this to hide our Intellectual Property from the user.

      // Strip translation instruction from the message for display
      let displayMessage = stripTranslationInstruction(interaction?.message || "");

      const questionObj = {
        id: interaction?.id,
        type: interaction?.from === "user" ? "prompt" : "response",
        message: displayMessage,
        error: interaction?.status || null,
      };

      if (i === 0 && presetPrompt) {
        questionObj.message = presetPrompt;
      }

      if (interaction?.from === "assistant") {
        // Strip translation instruction from assistant response as well (just in case)
        const responseMessage = stripTranslationInstruction(interaction?.message || "");
        const responseObj = {
          id: interaction?.id,
          type: "response",
          message: responseMessage,
          error: interaction.status || null,
          server: {},
        };
        return [questionObj, responseObj];
      }

      return [questionObj];
    });

    // Remove any duplicate id's
    const uniqueMessages = parsedMessages.filter(
      (msg, index, self) => index === self.findIndex((t) => t.id === msg.id)
    );

    setMessagesByRoom((prev) => ({
      ...prev,
      [id]: uniqueMessages,
    }));
  };

  // Step 1 (New Chat): Initiate chat
  const initiateChat = (promptQuery, feedbackData, feedbackParams) => {
    dispatch({ type: CLEAR_CHAT_MESSAGES }); // Clear message history
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
    });

    const messageId = uuidv4();

    // Translation instruction to append to LLM prompt (not visible to user)
    const targetLanguage = language === 'fr' ? 'French' : 'English';
    const translationInstruction = `\n\nAlways return all responses in  ${targetLanguage}, If returning verbatim feedback, make sure all feedback are translated into ${targetLanguage}.`;

    // Store original prompt for display
    const displayMessage = promptQuery;
    // Append instruction for LLM
    const llmMessage = promptQuery + translationInstruction;

    const payload = {
      token: chatToken,
      name: generateTopicName(displayMessage),
      initial_message: {
        id: messageId,
        message: llmMessage, // Send to LLM with instruction
        from: "user",
        type: "user",
        user: chatUserName,
        timestamp: new Date().toISOString(),
      },
      contextData: feedbackData,
      params: feedbackParams,
      status: "active",
    };

    const initialMessageId = `msg-${Date.now()}`;
    const initialMessage = {
      id: initialMessageId,
      message: displayMessage, // Store original for display
      from: "user",
      username: chatUserName,
      timestamp: new Date().toISOString(),
    };
    
    setMessagePending(true);

    setPendingInitialMessage(initialMessage);

    dispatch({
      type: SG_CREATE_CHAT_ROOM_V2,
      payload: payload,
    });

    setChatStarted(true);
    setPromptText("");
  };

  // Step 2: Handle room creation and WebSocket connection
  useEffect(() => {
    if (!createdRoom?.id || !pendingInitialMessage) return;

    const roomId = createdRoom.id;
    // First disconnect any existing connection
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
    });
    // Set new room ID
    setSelectedRoomId(roomId);
    // Connect WebSocket
    dispatch({
      type: SAGA_CONNECT_WEBSOCKET_V2,
      payload: { web_chat_id: roomId },
    });
    // Fetch room details
    dispatch({
      type: SG_FETCH_CHAT_ROOM_V2,
      payload: { id: roomId },
    });
  }, [createdRoom, pendingInitialMessage, selectedRoomId]);

  // Step 3: Send initial message after connection
  useEffect(() => {
    if (wsConnected && pendingInitialMessage && selectedRoomId) {
      // Translation instruction to append to LLM prompt (not visible to user)
      const targetLanguage = language === 'fr' ? 'French' : 'English';
      const translationInstruction = `\n\nAlways return all responses in  ${targetLanguage}, If returning verbatim feedback, make sure all feedback are translated into ${targetLanguage}.`;

      // Store original message for display
      const displayMessage = pendingInitialMessage.message.trim();
      // Append instruction for LLM
      const llmMessage = displayMessage + translationInstruction;

      // Payload for display (original message)
      const displayPayload = {
        id: `msg-${Date.now()}`,
        message: displayMessage,
        web_chat_id: selectedRoomId,
        from: "user",
        username: chatUserName,
        timestamp: new Date().toISOString(),
        type: "user",
      };

      // Payload for LLM (with instruction)
      const llmPayload = {
        ...displayPayload,
        message: llmMessage,
      };

      // First dispatch the original message to local state (for display)
      dispatch({
        type: MESSAGE_WEBSOCKET,
        payload: displayPayload,
      });

      // Then send message with instruction through websocket to LLM
      dispatch({
        type: SAGA_MESSAGE_WEBSOCKET_V2,
        payload: llmPayload,
      });
      // Cleanup
      dispatch({ type: CLEAR_CREATED_ROOM });
      setPendingInitialMessage(null);
    }
  }, [wsConnected, pendingInitialMessage, selectedRoomId, chatUserName, language]);

  useEffect(() => {
    if (currentRoom) {
      handleFetchedRoomData(currentRoom);
    }
  }, [currentRoom, selectedRoomId]);

  // Add this function to save scroll position before updates
  const saveScrollPosition = () => {
    if (isSmoothScrollingRef.current) return;

    lastScrollPositionRef.current = {
      position: window.scrollY,
      height: document.body.scrollHeight,
    };
  };

  // Add this function to restore scroll position after updates
  const restoreScrollPosition = (shouldScrollToBottom = false) => {
    // Short delay to allow DOM to update
    setTimeout(() => {
      if (!lastScrollPositionRef.current) return;

      const { position, height } = lastScrollPositionRef.current;
      const newHeight = document.body.scrollHeight;
      const heightDiff = newHeight - height;

      if (shouldScrollToBottom) {
        // Scroll to bottom with smooth behavior
        isSmoothScrollingRef.current = true;
        if (messagesEndRef?.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
        setTimeout(() => {
          isSmoothScrollingRef.current = false;
        }, 500); // Duration slightly longer than scroll animation
      } else if (heightDiff > 0) {
        // Maintain relative position if content expanded
        window.scrollTo(0, position + heightDiff);
      } else {
        // Otherwise restore original position
        window.scrollTo(0, position);
      }

      lastScrollPositionRef.current = null;
    }, 10);
  };

  // Regular Room Selection Handler
  const handleRoomSelect = useCallback(
    (roomId) => {
      if (selectedRoomId) {
        dispatch({
          type: DISCONNECT_WEBSOCKET_V2,
          payload: { web_chat_id: selectedRoomId },
        });
      }

      // Set new room ID
      setSelectedRoomId(roomId);

      // Connect WebSocket for existing room
      dispatch({
        type: SAGA_CONNECT_WEBSOCKET_V2,
        payload: { web_chat_id: roomId },
      });

      // Fetch room history
      dispatch({
        type: SG_FETCH_CHAT_ROOM_V2,
        payload: { id: roomId },
      });

      // Set chat started
      setChatStarted(true);
    },
    [dispatch, selectedRoomId]
  );

  const handleDeleteRoom = (roomId) => {
    const isActiveRoom = selectedRoomId === roomId;

    // Disconnect the current websocket connection
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
      payload: { web_chat_id: selectedRoomId },
    });

    // Delete the room
    dispatch({ type: SG_DELETE_CHAT_ROOM, payload: { id: roomId } });

    // If we're deleting the active room, reset everything
    if (isActiveRoom) {
      setSelectedRoomId(null);
      setPromptText("");
      setChatStarted(false);
      setSelectedParams(null);

      // Reset params.
      dispatch({ type: CLEAR_CHAT_MESSAGES });
      dispatch({ type: SET_NEW_FILTERS, payload: [] });
      dispatch({ type: CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD });
      dispatch({ type: START_NEW_CHAT });
    }

    // Refresh list of rooms after delete
    dispatch({ type: SG_GET_CHAT_ROOM_V2 });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const cleanup = () => {
    setSelectedParams(null);
    dispatch({ type: START_NEW_CHAT });
  };

  // Load selected data source into Redux (copied from NewAnalytics)
  const loadDataSource = async (dataSource, type, sourceLocation = 'analytics') => {
    console.log("=== COPILOT LOADING DATA SOURCE ===");
    console.log("Type:", type);
    console.log("Source Location:", sourceLocation);
    console.log("DataSource:", dataSource);
    console.log("Total available responses:", get_culture_audit_reports.response?.length);
    
    try {
      let responses = [];
      
      if (type === 'sortOrder') {
        responses = get_culture_audit_reports.response.filter(
          r => r.question?.sort_order === dataSource.sortOrder
        );
        console.log(`Filtered by sort order ${dataSource.sortOrder}:`, responses.length, "responses");
      } else if (type === 'debrief') {
        responses = dataSource.responses;
        console.log("Using debrief responses:", responses.length, "responses");
      }
      
      console.log("Raw responses before processing:", responses.length);
      
      // Apply personality conversion if needed
      if (checkPersonality(get_organizations)) {
        responses = convert_personality_to_categories(responses);
      }
      
      // Process the data similar to LeaderDataLoader
      let sorted_responses = responses.sort((a, b) => b.id - a.id);
      let most_recent = get_most_recent(sorted_responses);
      most_recent["last"] = get_last_responses(sorted_responses);

      
      if (sorted_responses.length > 0 && most_recent.responses) {
        let historical_data = sort_historical_data(sorted_responses.reverse(), 0);
        const processedData = [most_recent, ...historical_data];
        
        console.log("=== PROCESSED DATA ===");
        console.log("Most recent responses:", most_recent.responses?.length);
        console.log("Historical data length:", historical_data.length);
        console.log("Final processed data:", processedData);
        
        // Add metadata with survey name and group info
        processedData[0].datasetMetadata = {
          timestamp: Date.now(),
          source: `${type}-${dataSource.sortOrder || dataSource.id}`,
          originalFilteredCount: responses.length,
          selectedSortOrder: dataSource.sortOrder,
          selectionType: type,
          surveyName: dataSource.questionTitle || dataSource.title || (responses[0]?.question?.title) || `Survey ${dataSource.sortOrder || dataSource.id}`,
          groupTitle: dataSource.groupTitle || null,
          sourceLocation: sourceLocation, // Track if loaded from 'drawer' or 'analytics'
          originalDrawerId: sourceLocation === 'drawer' ? dataSource.id : undefined // Store original drawer ID for removal
        };
        
        // Set up outcome questions for dashboard score cards BEFORE dispatching to Redux
        console.log("=== SETTING UP OUTCOME QUESTIONS ===");
        console.log("Organization ID:", organizationId);
        console.log("Organizations data:", get_organizations);
        console.log("Filtered responses:", responses?.length);
        console.log("Outcomes questions list:", outcomesQuestionsList?.length);
        
        if (outcomesQuestionsList?.length > 0) {
          let outcomeSortOrder = null;
          
          // Priority 1: Check for outcome_question in the filtered survey responses' additional_questions
          if (responses?.length > 0) {
            // Look for outcome_question in the filtered survey responses (could be multiple responses)
            // Use the first response that has additional_questions.outcome_question defined
            const responseWithOutcome = responses.find(response => 
              response?.additional_questions?.outcome_question?.question?.sort_order
            );
            
            if (responseWithOutcome) {
              outcomeSortOrder = responseWithOutcome.additional_questions.outcome_question.question.sort_order;
              console.log("Found outcome sort order from survey response additional_questions:", outcomeSortOrder);
              console.log("Source response:", responseWithOutcome.id || 'Unknown ID');
            }
          }
          
          // Priority 2: Fallback to get_organizations configuration
          if (!outcomeSortOrder && get_organizations && organizationId) {
            outcomeSortOrder = get_organizations?.[organizationId]?.styling?.survey_sequence?.find(
              (f) => f.value === "outcome_question"
            )?.question?.sort_order;
            console.log("Found outcome sort order from organizations (fallback):", outcomeSortOrder);
          }
          
          if (outcomeSortOrder) {
            const outcomeQ = outcomesQuestionsList.find(
              (f) => f.sort_order === outcomeSortOrder
            );
            
            console.log("Found outcome questions:", outcomeQ);
            
            // Store outcome questions in metadata for Dashboard to use
            processedData[0].datasetMetadata.outcomeQuestions = outcomeQ;
            console.log("Added outcome questions to metadata");
          } else {
            console.log("No outcome sort order found from either source");
          }
        }
        console.log("=== END OUTCOME SETUP ===");
        
        console.log("=== METADATA ATTACHED ===");
        console.log("Final metadata:", processedData[0].datasetMetadata);
        
        // Dispatch to Redux
        dispatch({
          type: SET_RAW_DATA,
          payload: processedData,
        });
        
        dispatch({
          type: SET_DATA_DATES,
          payload: get_dates(historical_data),
        });
        
        // Set up navigation state if we have survey structure
        if (get_survey_structure?.[0]) {
          const DefState = build_default_State(get_survey_structure[0]);
          dispatch({
            type: SET_NAVIGATION,
            payload: DefState,
          });
          
          // Apply initial filtering
          if (nav_state && Object.keys(nav_state).length > 0) {
            let filtered_data = filter_data(
              processedData,
              nav_state,
              get_survey_structure[0]
            );
            
            dispatch({
              type: SET_FILTERED_DATA,
              payload: filtered_data.filter_data,
            });
            
            dispatch({
              type: SET_FILTERED_LAST_DATA,
              payload: filtered_data.filter_data_last,
            });
          }
        }

        console.log("=== COPILOT DATA LOADED SUCCESSFULLY ===");
        // Only set custom dataset flag if this came from analytics, not manual drawer selection
        // Drawer selections are tracked in selectedSurveys separately
        if (sourceLocation !== 'drawer') {
          setShouldUseCustomDataset(true);
        }
      }
    } catch (error) {
      console.error('Error loading data source in copilot:', error);
    }
  };

  // Function to start a new conversation
  const startNewConversation = () => {
    // setChatHistory([]);
    setSelectedRoomId(null);
    setPromptText("");
    setChatStarted(false);

    // Reset params.
    dispatch({ type: CLEAR_CHAT_MESSAGES });
    dispatch({ type: SET_NEW_FILTERS, payload: [] });
    dispatch({ type: CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD });
    // Clear Redux survey data
    dispatch({ type: SET_RAW_DATA, payload: [] });
    dispatch({ type: SET_FILTERED_DATA, payload: [] });
    setShouldUseCustomDataset(false);
    // Disconnect the current websocket connection
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
      payload: { web_chat_id: selectedRoomId },
    });

    dispatch({
      type: START_NEW_CHAT,
    });

    setSelectedParams(null);
  };

  const handleSendMessage = useCallback(
    (messageText) => {
      if (!wsConnected || !selectedRoomId) {
        console.error("Cannot send message:", {
          connected: wsConnected,
          hasWebChatId: !!selectedRoomId,
        });
        return;
      }

      // Translation instruction to append to LLM prompt (not visible to user)
      const targetLanguage = language === 'fr' ? 'French' : 'English';
      const translationInstruction = `\n\nAlways return all responses in  ${targetLanguage}, If returning verbatim feedback, make sure all feedback are translated into ${targetLanguage}.`;

      // Store original message for display
      const displayMessage = messageText.trim();
      // Append instruction for LLM
      const llmMessage = displayMessage + translationInstruction;

      // Payload for display (original message)
      const displayPayload = {
        id: `msg-${Date.now()}`,
        message: displayMessage,
        web_chat_id: selectedRoomId,
        from: "user",
        username: chatUserName,
        timestamp: new Date().toISOString(),
        status: "complete",
      };

      // Payload for LLM (with instruction)
      const llmPayload = {
        ...displayPayload,
        message: llmMessage,
      };

      // Store original message for display
      dispatch({
        type: MESSAGE_WEBSOCKET,
        payload: displayPayload,
      });
      // Send message with instruction to LLM
      dispatch({
        type: SAGA_MESSAGE_WEBSOCKET_V2,
        payload: llmPayload,
      });
      addPromptToHistoryIfNeeded(chatHistory, setChatHistory, promptText);
      setMessagePending(true);

      setPromptText("");
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    },
    [wsConnected, selectedRoomId, chatUserName, dispatch, language]
  );

  const reconnect = useCallback(() => {
    if (!selectedRoomId) {
      console.error("Cannot reconnect: No room ID selected");
      return;
    }

    // First disconnect any existing connection
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
    });

    // Then reconnect using the current room ID
    dispatch({
      type: SAGA_CONNECT_WEBSOCKET_V2,
      payload: { web_chat_id: selectedRoomId },
    });
  }, [dispatch, selectedRoomId]);

  return (
    <CopilotDataContext.Provider
      value={{
        chatRooms,
        currentRoom,
        chatStarted,
        promptText,
        chatUserName,
        wsConnected,
        wsError,
        chatToken,
        handleRoomSelect,
        handleDeleteRoom,
        initiateChat,
        handlePageChange,
        setPromptText,
        startNewConversation,
        selectedRoomId,
        messagesEndRef,
        messagesByRoom,
        handleSendMessage,
        pendingInitialMessage,
        messagePending,
        selectedParams,
        streamingMsg: messages,
        initialPendingId,
        usedPrompt,
        feedbackLength,
        setSelectedParams,
        cleanup,
        reconnect,
        isWebSocketConnected: wsConnected,
        // New selector data
        get_culture_audit_reports,
        get_organizations,
        pulseSurveys,
        get_auth,
        existingRawData,
        allowAnalyticsDataset,
        shouldUseCustomDataset,
        hasCustomDataset,
        // Data loading function
        loadDataSource,
      }}
    >
      {children}
    </CopilotDataContext.Provider>
  );
};
